import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faFileExcel, faSpinner, faArrowLeft, faUndo } from '@fortawesome/free-solid-svg-icons';
import { bulkUploadContracts } from './ContractApi';

const ContractBulkUpload: React.FC = () => {
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [failures, setFailures] = useState<any[]>([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const validTypes = [
        'text/csv',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      ];
      if (!validTypes.includes(selectedFile.type)) {
        setError('Only CSV or Excel files are allowed.');
        setFile(null);
      } else {
        setFile(selectedFile);
        setError(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }

    try {
      setUploading(true);
      setMessage(null);
      setError(null);
      setFailures([]);

      const response = await bulkUploadContracts(file);
      const { message, failures } = response.data;

      setMessage(message || 'Upload successful!');
      setFailures(failures || []);
      setUploadSuccess(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setMessage(null);
    setError(null);
    setFailures([]);
    setUploadSuccess(false);
    setUploading(false);
  };

  return (
    <div className="p-6 bg-white rounded shadow-md max-w-xl mx-auto">
      <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <FontAwesomeIcon icon={faFileExcel} />
        Bulk Upload Contracts
      </h2>

      {!uploadSuccess && (
        <>
          <input
            type="file"
            accept=".csv, .xlsx, .xls"
            onChange={handleFileChange}
            className="mb-4 block"
          />

          {file && (
            <div className="mb-2 text-sm text-gray-700">
              <strong>Selected:</strong> {file.name}
            </div>
          )}
        </>
      )}

      {error && <div className="text-red-600 mb-2">{error}</div>}
      {message && <div className="text-green-600 mb-2">{message}</div>}

      {failures.length > 0 && (
        <div className="mt-4 bg-red-50 border border-red-300 text-red-800 p-4 rounded text-sm">
          <h3 className="font-bold mb-2">Upload Failures:</h3>
          <ul className="list-disc pl-6 space-y-1 max-h-60 overflow-auto">
            {failures.map((fail, index) => (
              <li key={index}>
                <strong>{fail.refId || `Row ${index + 1}`}</strong>: {fail.reason || 'Unknown error'}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4 mt-6">
        {!uploadSuccess && (
          <button
            onClick={handleUpload}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2"
            disabled={uploading}
          >
            {uploading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                Uploading...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faUpload} />
                Upload
              </>
            )}
          </button>
        )}

        <button
          onClick={handleReset}
          className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600 flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faUndo} />
          Reset
        </button>

        <button
          onClick={() => navigate('/contracts/list')}
          className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700 flex items-center gap-2"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back
        </button>
      </div>
    </div>
  );
};

export default ContractBulkUpload;